import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useAuth } from "../hooks/useAuth";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { COPYRIGHT_TEXT } from "../constants/legal";
import AiChatWidget from "../components/common/AiChatWidget";
import { useModules } from "../theme/ModuleContext";

/**
 * AdminLayout — responsive shell for the admin panel.
 *
 *   Layers:
 *     <Sidebar/>   — fixed left rail (desktop) or slide-in drawer (mobile)
 *     <Header/>    — fixed top bar with theme picker + profile menu
 *     <main>       — scrolls under header, padded on left for the sidebar
 *
 *   The Header is themed (dark: classes) so everything picks up the theme
 *   toggled from its UI. Main content background also dark-mode aware so
 *   every page inherits the theme without per-page edits.
 */
const STORAGE_KEY = "prosale:sidebar-collapsed";

export default function AdminLayout({ children }) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { logout } = useAuth() || {};
  const { isModuleEnabled } = useModules();

  // Persisted desktop collapse state
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* noop */
    }
  }, [collapsed]);

  // Mobile drawer open/closed
  const [mobileOpen, setMobileOpen] = useState(false);

  const mainPadLeft = isMobile ? "pl-0" : collapsed ? "md:pl-20" : "md:pl-64";

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-950 dark:text-gray-100 transition-colors">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        onLogout={logout}
      />

      <Header
        isMobile={isMobile}
        onOpenMobileSidebar={() => setMobileOpen(true)}
        sidebarCollapsed={collapsed}
      />

      <main
        className={`${mainPadLeft} min-h-screen pt-16 bg-gray-50 dark:bg-slate-950 transition-[padding] duration-300 ease-in-out`}
      >
        <div className="min-h-[calc(100vh-8rem)] p-4 md:p-6">{children ?? <Outlet />}</div>
        <footer className="px-4 md:px-6 pb-5 text-center text-xs text-gray-400 dark:text-gray-500">
          {COPYRIGHT_TEXT}
        </footer>
      </main>
      {isModuleEnabled("ai") && <AiChatWidget />}
    </div>
  );
}
